import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDropzone } from 'react-dropzone';
import { X, Paperclip, FolderInput, ArrowLeft, FileUp } from 'lucide-react';

import { UPLOAD_SIZE_LIMIT } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { type AssetContentTypeEnum } from 'src/graphql/schema';

type UploadProgress = { percent: number; currentCount: number };

type UploadCompletionResult = { status: 'success' } | { status: 'canceled' } | { status: 'error'; message: string };

type SecureUploaderProps = {
  resetModal: () => void;
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  back?: () => void;
  contentType: AssetContentTypeEnum;
  uploadFiles: (files: File[], contentType: AssetContentTypeEnum) => Promise<unknown>;
  cancelUpload: () => void;
  uploadProgress: UploadProgress;
  onUploadComplete: (result: UploadCompletionResult) => void;
  onFileRejected: (message: string) => void;
};

const UploadProgressView = ({
  uploadProgress,
  onDiscard,
  total,
}: {
  uploadProgress: UploadProgress;
  onDiscard: () => void;
  total: number;
}) => {
  return (
    <div className="space-y-6">
      <div className="border border-border border-dashed bg-white rounded-lg p-6 min-h-[192px] flex items-center justify-center">
        <div className="w-[400px] max-w-full text-center space-y-2.5">
          <FileUp className="size-9 mx-auto" />
          <Progress value={uploadProgress.percent} className="h-4" />
          <span className="text-sm text-foreground font-semibold">
            Uploading: {uploadProgress.currentCount} / {total}
          </span>
        </div>
      </div>
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onDiscard}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const formattedSizeLimit = formatBytes(UPLOAD_SIZE_LIMIT, 0);

export function SecureUploader({
  resetModal,
  isUploading,
  setIsUploading,
  back,
  contentType,
  uploadFiles,
  cancelUpload,
  uploadProgress,
  onUploadComplete,
  onFileRejected,
}: SecureUploaderProps) {
  const [files, setFiles] = useState<File[]>([]);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => setFiles((files: File[]) => [...files, ...acceptedFiles].slice(0, 5)),
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0].errors[0];
      let message = error.message;

      if (error.code === 'file-too-large') {
        message = `File size exceeds the limit of ${formattedSizeLimit}. Please upload a smaller file.`;
      }

      onFileRejected(message);
    },
    maxFiles: 5,
    maxSize: UPLOAD_SIZE_LIMIT,
  });

  const uploadSelectedFiles = async () => {
    if (isUploading) return;

    setIsUploading(true);

    try {
      await uploadFiles(files, contentType);
      setIsUploading(false);
      resetModal();
      setFiles([]);
      onUploadComplete({ status: 'success' });
    } catch (e) {
      if ((e as Error).name === 'CanceledError') {
        Sentry.captureException(e);
        onUploadComplete({ status: 'canceled' });
      } else {
        onUploadComplete({ status: 'error', message: (e as Error).message });
      }
    } finally {
      setIsUploading(false);
    }
  };
  const onDiscard = () => {
    setIsUploading(false);
    cancelUpload();
  };

  const removeFile = (file: File) => setFiles((prevFiles) => prevFiles.filter(({ name }) => name !== file.name));

  if (isUploading)
    return <UploadProgressView uploadProgress={uploadProgress} onDiscard={onDiscard} total={files.length} />;

  return (
    <section className="min-w-0">
      <div className="space-y-6 px-6 mb-6">
        <div
          {...getRootProps()}
          className="px-6 py-10 border border-border border-dashed bg-muted/60 rounded-lg flex flex-col items-center cursor-pointer hover:bg-muted/100 transition-colors"
        >
          <input {...getInputProps()} />
          <FolderInput className="size-9 mb-2.5" />
          <p className="text-sm font-semibold mb-1.5">Drag and drop files</p>
          <span className="text-sm text-muted-foreground mb-2.5">or</span>
          <Button size="sm">Browse files</Button>
        </div>
        <p className="italic text-sm text-muted-foreground">The maximum upload size is {formattedSizeLimit}.</p>
        {Boolean(files.length) && (
          <aside>
            <ul className="space-y-4">
              {files.map((file) => {
                const handleRemove = () => removeFile(file);

                return (
                  <li key={file.name} className="flex items-center gap-4 px-4 py-1.5 bg-accent rounded-md">
                    <Paperclip className="size-5 shrink-0" />
                    <div className="grow-1 text-foreground text-sm truncate">{file.name}</div>
                    <Button className="shrink-0" variant="ghost" size="icon" onClick={handleRemove}>
                      <X className="size-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </aside>
        )}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={back}>
          <ArrowLeft />
          Back
        </Button>
        <Button disabled={!files.length} onClick={uploadSelectedFiles} isLoading={isUploading}>
          Add
        </Button>
      </div>
    </section>
  );
}

export type { SecureUploaderProps, UploadProgress, UploadCompletionResult };
